import { Popover, Transition } from '@headlessui/react';
import { prebuiltSegments } from 'components/segments/prebuiltSegments';
import { ButtonCTASmall, LoadingSpinner, Tooltip } from 'components/ui';
import { Fragment, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useUpdateSegmentMutation } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';

export const EditSegment = () => {
  const [segmentName, setSegmentName] = useState('');

  const { serializedStoreName } = useParseURLQueryParams();

  const { selected, data } = useAppSelector(composableFiltersState);

  const currentSegment = selected.segment;

  const isSegmentPrebuilt = prebuiltSegments.includes(
    String(currentSegment?.segment_name)
  );

  const isManagedSegment = Boolean(
    currentSegment?.json_filter?.filter?.filters?.find((filter) =>
      filter.name?.startsWith('managed_segment')
    )
  );

  const [updateSegment, { isLoading }] = useUpdateSegmentMutation();

  const handleEdit = (close: Function) => {
    const isNameAlreadyUsed = data.segments?.find(
      (segment) => segment.segment_name === segmentName
    );

    if (isNameAlreadyUsed) {
      toast.error('Segment name already used');
      return;
    }

    const body = {
      segment_name: segmentName,
    };

    updateSegment({
      body,
      segmentId: Number(currentSegment?.id),
      serializedStoreName: String(serializedStoreName),
    })
      .unwrap()
      .then(() => {
        toast.success(`Segment was renamed`);
        close();
      });
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`
                ${open ? '' : 'text-opacity-90'}
                text-segments-link group px-3 py-2 text-base font-medium hover:text-opacity-100 disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={isSegmentPrebuilt || isManagedSegment}
          >
            <Tooltip
              text={
                isManagedSegment || isSegmentPrebuilt
                  ? 'Pre-built / premium segments can not be renamed'
                  : 'Rename'
              }
            >
              <EditIcon />
            </Tooltip>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm mt-2 transform -translate-x-1/2 left-1/2 lg:max-w-sm ">
              <div className="bg-white shadow-2xl rounded-3xl ring-1 ring-black ring-opacity-5">
                <div className="pt-6 px-7">
                  <p className="font-bold">Rename segment</p>
                </div>
                <form
                  className="px-6 py-2 text-gray-700"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleEdit(close);
                  }}
                >
                  <div className="mt-2">
                    <input
                      tabIndex={0}
                      type="text"
                      id="segment-name"
                      className="flex-1 w-full h-12 px-5 placeholder-black border border-black border-solid border-opacity-20 rounded-segments-10px placeholder-opacity-30 focus:border-segments-accent-color-1 focus:ring-segments-accent-color-1 disabled:bg-segments-gray"
                      value={segmentName}
                      onChange={(e) => setSegmentName(e.target.value)}
                      spellCheck="false"
                      required
                      placeholder="New name"
                    />
                  </div>

                  <div className="flex justify-end mt-6 mb-2 space-x-4">
                    <div>
                      <ButtonCTASmall
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          close();
                        }}
                      >
                        <p className="px-4 font-normal text-segments-13px">
                          Cancel
                        </p>
                      </ButtonCTASmall>
                    </div>
                    <div>
                      <ButtonCTASmall
                        variant="main"
                        type="submit"
                        disabled={!!!segmentName}
                      >
                        <div className="w-20 font-bold text-segments-13px">
                          {isLoading ? (
                            <LoadingSpinner className="w-5 h-5 mx-auto text-white" />
                          ) : (
                            'Update'
                          )}
                        </div>
                      </ButtonCTASmall>
                    </div>
                  </div>
                </form>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`w-5 h-5 ${className}}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
    >
      <path
        d="M15 8.75v7.188a1.562 1.562 0 01-1.563 1.562H4.063A1.563 1.563 0 012.5 15.937V6.563A1.562 1.562 0 014.063 5h6.542"
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.967 2.08a.627.627 0 00-.907-.022l-.484.481a.313.313 0 000 .442l.443.442a.311.311 0 00.443 0l.471-.469a.635.635 0 00.034-.874zM15.6 3.516l-7.052 7.039a.35.35 0 00-.09.153l-.327.972a.153.153 0 00.19.19l.97-.327a.351.351 0 00.154-.09l7.04-7.052a.352.352 0 000-.495l-.39-.39a.351.351 0 00-.496 0z"
        fill="#000"
      />
    </svg>
  );
}
